import { useEffect, useRef, Children, cloneElement, ReactElement, useState, Fragment } from "react";
import { createPortal } from "react-dom";

export default function Tooltip({
  // id,
  show,
  children,
  text,
}: {
  // id: string;
  show?: boolean;
  text: string;
  children: ReactElement;
}) {
  const parentElementRef = useRef<HTMLDivElement | null>(null);
  const toolTipElmRef = useRef<HTMLDivElement | null>(null);
  const toolTipArrowRef = useRef<HTMLDivElement | null>(null);

  const [innerShow, setInnerShow] = useState(false);

  // Internal methods
  function positionTooltip(parentElm: HTMLElement, _toolTipElm: HTMLElement) {
    const { y: parentTop, x: parentLeft, width: parentWidth, height: parentHeight } = parentElm.getBoundingClientRect();
    const { height: tooltipHeight, width: tooltipWidth } = _toolTipElm.getBoundingClientRect();

    const gap = 10;
    const margin = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Horizontal: center over parent, clamped to the viewport edges
    const centeredLeft = parentLeft + (parentWidth - tooltipWidth) / 2;
    const left = Math.max(margin, Math.min(centeredLeft, viewportWidth - tooltipWidth - margin));

    // Vertical: prefer above, flip below when there is no space on top
    let top = parentTop - tooltipHeight - gap;
    let placement: "top" | "bottom" = "top";

    if (top < margin) {
      top = parentTop + parentHeight + gap;
      placement = "bottom";
    }

    // Fallback if it still overflows the bottom
    if (top + tooltipHeight > viewportHeight - margin) {
      top = parentTop - tooltipHeight - gap;
      placement = "top";
    }

    _toolTipElm.style.top = `${top}px`;
    _toolTipElm.style.left = `${left}px`;

    // Keep the arrow pointing at the parent center
    const arrow = toolTipArrowRef.current;
    if (arrow) {
      const offsetX = centeredLeft - left;
      arrow.style.left = `calc(50% + ${offsetX}px)`;
      arrow.style.borderLeft = "5px solid transparent";
      arrow.style.borderRight = "5px solid transparent";

      if (placement === "bottom") {
        arrow.style.top = "auto";
        arrow.style.bottom = "100%";
        arrow.style.borderTop = "none";
        arrow.style.borderBottom = "5px solid #000";
      } else {
        arrow.style.top = "100%";
        arrow.style.bottom = "auto";
        arrow.style.borderTop = "5px solid #000";
        arrow.style.borderBottom = "none";
      }
    }
  }

  // Portal
  const tooltip = createPortal(
    <div
      ref={toolTipElmRef}
      style={{
        zIndex: (show != undefined ? show : innerShow) ? "9999" : "-1000",
        opacity: (show != undefined ? show : innerShow) ? 1 : 0,
      }}
      className="absolute text-sm p-1 px-2 rounded-md bg-black text-white transition-all"
    >
      {text}
      <div
        ref={toolTipArrowRef}
        className="absolute"
        style={{
          width: 0,
          height: 0,
          borderLeft: "5px solid transparent",
          borderRight: "5px solid transparent",
          borderTop: "5px solid #000",
          top: "100%",
          left: "50%",
          transform: "translateX(-50%)",
        }}
      ></div>
    </div>,
    document.body
  );

  // Mount
  useEffect(() => {
    let lastDevicePixelRatio = window.devicePixelRatio;

    function onMouseOver() {
      // Adjust position
      if (parentElementRef.current && toolTipElmRef.current) positionTooltip(parentElementRef.current, toolTipElmRef.current);

      setInnerShow(true);
    }

    function onMouseOut() {
      setInnerShow(false);
    }

    function handleZoomChange() {
      console.log("Zoom changed", window.devicePixelRatio, lastDevicePixelRatio);
      if (window.devicePixelRatio !== lastDevicePixelRatio) {
        lastDevicePixelRatio = window.devicePixelRatio;

        // Reposition the tooltip
        if (parentElementRef.current && toolTipElmRef.current) {
          positionTooltip(parentElementRef.current, toolTipElmRef.current);
        }
      }
    }

    function handleWindowSizeChange() {
      if (parentElementRef.current && toolTipElmRef.current) positionTooltip(parentElementRef.current, toolTipElmRef.current);
    }

    if (parentElementRef.current && toolTipElmRef.current) {
      handleWindowSizeChange();
      window.addEventListener("resize", handleWindowSizeChange);

      // Bind to hover
      if (show === undefined) {
        parentElementRef.current.addEventListener("mouseover", onMouseOver);
        parentElementRef.current.addEventListener("mouseout", onMouseOut);
        window.removeEventListener("resize", handleWindowSizeChange);
      }
    }

    window.addEventListener("resize", handleZoomChange);

    return () => {
      window.removeEventListener("resize", handleZoomChange);
      parentElementRef.current?.removeEventListener("mouseover", onMouseOver);
      parentElementRef.current?.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  // Get element
  // const parentChild = Children.only(children);

  /* {
     cloneElement(parentChild, {
       ref: (ref: any) => (parentElementRef.current = ref),
     });
   } */

  return (
    <div ref={parentElementRef}>
      {children}
      {tooltip}
    </div>
  );
}
